import moment from 'moment';
import { municipalities as municipalityMap } from '../../context/municipalities';
import * as BBApi from '.';

const municipalities = Object.entries(municipalityMap).reduce((municipalities, [name, id]) => {
	municipalities[id] = name;
	return municipalities;
}, {} as { [key: number]: string });

const rootUrl: string = BBApi.host + '/posts';

export async function query(
	params: Record<string, any>
): Promise<{ posts: Record<string, any>[]; totalMatches: number }> {
	const url: URL = new URL(rootUrl);

	url.searchParams.append('offset', String(0));
	url.searchParams.append('amount', String(params.amount));

	if (params.postId !== '') url.searchParams.append('postId', params.postId);
	if (params.status !== 'all') url.searchParams.append('status', params.status);
	if (params.country !== 'all') url.searchParams.append('country', params.country);

	if (Boolean(params.ageFrom)) url.searchParams.append('ageFrom', params.ageFrom);
	if (Boolean(params.ageUntil)) url.searchParams.append('ageUntil', params.ageUntil);

	const [dateFrom, dateTo] = params.date ?? [null, null];
	if (dateFrom !== null) url.searchParams.append('dateFrom', dateFrom.format('YYYY-MM-DD'));
	if (dateTo !== null) url.searchParams.append('dateUntil', dateTo.format('YYYY-MM-DD'));
	if (params.compact === 'true') url.searchParams.append('compact', 'true');

	if (params.filter1.target !== 'all') url.searchParams.append('filter1', params.filter1.target);
	if (params.filter1.search !== '')
		url.searchParams.append('filterSearch1', `%${params.filter1.search}%`);

	if (params.filter2.target !== 'all') url.searchParams.append('filter2', params.filter2.target);
	if (params.filter2.search !== '')
		url.searchParams.append('filterSearch2', `%${params.filter2.search}%`);

	const response: Response = await BBApi.get(url.href);
	const { posts, totalMatches }: { posts: Record<string, any>[]; totalMatches: number } =
		await response.json();

	posts.forEach((post) => {
		post.municipality = municipalities[post.municipality];
	});

	return { posts, totalMatches };
}

export async function get(id: number, query: any = {}) {
	const url = new URL(rootUrl + '/' + id);
	if (query.preview) url.searchParams.append('preview', '1');

	const response = await BBApi.get(url.href);
	const post = await response.json();

	if (post.expiry_date !== null) {
		post.expiry_date = moment(post.expiry_date);
	}

	if (post.image !== undefined) {
		post.image = 'data:image/jpg;base64,' + post.image;
	}

	return post;
}

export async function create(post: any): Promise<any> {
	throw new Error('Not implemented');
}

export async function update(id: number, post: Record<string, any>): Promise<void> {
	post.approved = Number(post.approved);
	post.priority = Number(post.priority);
	post.expiry_date = post.expiry_date?.toDate();

	const rawBase64: string | undefined = post?.image?.split(';base64,').pop();
	post.image = rawBase64;

	const response: Response = await BBApi.put(rootUrl + `/${id}`, post);

	if (response.ok === false) {
		throw new Error('Post not updated');
	}
}

export async function remove(id: number): Promise<void> {
	const response: Response = await BBApi.del(rootUrl + `/${id}`);

	if (response.ok === false) {
		throw new Error('Post not deleted');
	}
}

export async function moveToUnion(id: number, dependencies: Record<string, any>): Promise<number> {
	if (dependencies.url === '') dependencies.url = null;
	const response: Response = await BBApi.put(rootUrl + `/${id}/as-union`, dependencies);

	if (response.ok === false) {
		throw new Error('Post not moved');
	}

	const { unionId }: Record<string, any> = await response.json();
	return unionId;
}

export async function getConversations(postId: number): Promise<Record<string, any>[]> {
	const response: Response = await BBApi.get(rootUrl + `/${postId}/conversations`);
	const conversations: Record<string, any>[] = await response.json();
	return conversations;
}
